import * as Sentry from '@sentry/react'
import type { Unsubscribe } from 'firebase/firestore'
import {
  disableNetwork,
  doc,
  enableNetwork,
  getDocFromServer,
  onSnapshot,
} from 'firebase/firestore'
import React from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import { selectIsLoggedIn, selectToken } from '@store/actionSlices/token'

import {
  FULFILLED_STATUS,
  REJECTED_STATUS,
} from '@components/data-handler/data-handler'

import { useLazyHealthCheckQuery } from '@api/health-check'

import { getQueryStringParams } from '@adUtilities/helper'
import { hasToken } from '@adUtilities/token-helper'

import firestore from './firebase-db'

const withTimeout = <T>(p: Promise<T>, ms: number): Promise<T> =>
  Promise.race([
    p,
    new Promise<T>((_, reject) => {
      setTimeout(() => reject(new Error('probe-timeout')), ms)
    }),
  ])

const POLLING_INTERVAL = 30000
const PROBE_INTERVAL = 15000
const PROBE_TIMEOUT = 8000
const PROBE_BASE = '/release/'
const PROBE_DOC = 'showcase'

const initFirebaseSnapshot = (
  baseQuery: string,
  canPoll: boolean,
  ...queryInit: string[]
) => {
  const location = useLocation()
  const unsub = React.useRef<Unsubscribe | null>(null)

  const token = useSelector(selectToken)
  const isLoggedIn = useSelector(selectIsLoggedIn)

  const [enablePolling, setEnablePolling] = React.useState(false)
  const [firebaseIsActive, setFirebaseActive] = React.useState(false)
  const [pollingInterval, setPollingInterval] = React.useState(0)
  const [canReloadFbaseNetwork, setCanReloadFbaseNetwork] =
    React.useState(false)
  const [firebaseData, setFirebaseData] = React.useState<any>(undefined)

  const [fetch, { isLoading, isError, status }] = useLazyHealthCheckQuery({
    pollingInterval: isLoggedIn ? pollingInterval : 0,
  })

  const sendSentryReport = (message: string) => {
    Sentry.captureMessage(message, {
      level: 'error',
      tags: { source: 'firebase-snapshot' },
    })
  }

  const probeConnection = async (): Promise<boolean> => {
    if (!firestore) return false
    try {
      const snap = await withTimeout(
        getDocFromServer(doc(firestore, PROBE_BASE, PROBE_DOC)),
        PROBE_TIMEOUT
      )
      console.log('Firestore probe OK at:', new Date(), {
        exists: snap.exists(),
      })
      return true
    } catch (e) {
      setCanReloadFbaseNetwork(true)
      const message = 'Firestore probe failed/timed out — offline.'
      sendSentryReport(message)
      console.warn(message, new Date(), e)
      return false
    }
  }

  const createSnapShot = () => {
    if (firestore) {
      unsub.current = onSnapshot(
        doc(firestore, baseQuery, ...queryInit),
        // { includeMetadataChanges: true },
        (doc) => {
          if (!doc.exists()) {
            setFirebaseData(undefined)
            setFirebaseActive(false)
          }
          setFirebaseActive(true)
          setFirebaseData(doc.data())
        },
        (error) => {
          const message = 'Firebase snapshot listener error'
          sendSentryReport(message)
          console.error(message, error)
        }
      )
    }
  }

  const firebaseReload = async () => {
    unsub.current?.()
    try {
      await disableNetwork(firestore)
      await enableNetwork(firestore)
    } catch (e) {
      console.log('Firestore network reset failed: ', e)
    }
    createSnapShot()
    console.info(
      'Firestore has been reloaded! Network restored at: ',
      new Date()
    )
  }

  React.useEffect(() => {
    if (
      !isLoading &&
      isError &&
      !canReloadFbaseNetwork &&
      status === REJECTED_STATUS
    ) {
      setCanReloadFbaseNetwork(true)
      console.error(
        'Showcase Alert: Unable to reach the server. Please check your internet connection.'
      )
      return
    }
    if (
      !isLoading &&
      !isError &&
      canReloadFbaseNetwork &&
      status === FULFILLED_STATUS
    ) {
      firebaseReload()
      setCanReloadFbaseNetwork(false)
    }
    if (enablePolling) {
      console.log('Server Polling:(', status, ')Time:', new Date())
    }
  }, [isLoading, isError, canReloadFbaseNetwork, status])

  React.useEffect(() => {
    if (firebaseIsActive && enablePolling) {
      setPollingInterval(POLLING_INTERVAL)
      fetch()
      return
    }
    setPollingInterval(0)
  }, [firebaseIsActive, enablePolling])

  const validateQuery = (query: string[]) =>
    query.filter((res) => res).length >= 3

  React.useEffect(() => {
    if (!hasToken(token)) {
      unsub.current?.()
      return undefined
    }

    if (baseQuery && validateQuery(queryInit)) {
      createSnapShot()
    }
    return () => {
      unsub.current?.()
    }
  }, [token, [baseQuery, ...queryInit].toString()])

  React.useEffect(() => {
    if (!canPoll) {
      return undefined
    }
    if (!hasToken(token)) {
      return undefined
    }
    const id = setInterval(async () => {
      await probeConnection()
    }, PROBE_INTERVAL)
    return () => clearInterval(id)
  }, [token])

  React.useEffect(() => {
    if (!canPoll) {
      return undefined
    }
    const queryEnablePolling = getQueryStringParams(location.search)?.polling
    if (
      isLoggedIn &&
      (queryEnablePolling === 'true' || queryEnablePolling === 'false')
    ) {
      localStorage.setItem('polling', queryEnablePolling)
      setEnablePolling(queryEnablePolling === 'true')
      return undefined
    }
    const localStorageEnablePolling = localStorage.getItem('polling') === 'true'
    setEnablePolling(localStorageEnablePolling)
    return undefined
  }, [isLoggedIn])

  return firebaseData
}

export default initFirebaseSnapshot
